import { useEffect, useRef, useState, type JSX, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Chip } from "../design/ds";
import { feedbackApi } from "./api";
import type { RoutingResult } from "./model";
import {
  previewDueText,
  routingAssignee,
  routingMethod,
  routingWhy,
  type MethodPill,
} from "./supportFormat";
import { Pill } from "./ui";

/**
 * «Кому уйдёт вопрос» под формой «Вопрос в УК» (как в виджете CRM, «Поддержка партнёров»).
 * Пока человек печатает, CRM по тексту (слова → ИИ → отдел по умолчанию) показывает отдел,
 * ответственного и срок ответа; «Не туда? Выбрать отдел самому» — ручной выбор. Ничего не
 * сохраняет: при отправке бэкенд определяет всё заново (с deptCode — только если отдел
 * выбран вручную И это видно в карточке: `routedBy === 'manual'`).
 */

/** Короче — определять рано: «касса» ещё не вопрос. */
export const ROUTE_PREVIEW_MIN_CHARS = 12;
/** Пауза после последней буквы перед запросом. */
export const ROUTE_PREVIEW_DEBOUNCE_MS = 700;

export interface RoutePreviewState {
  /** Последний успешный ответ (может быть от прошлого текста, пока грузится новый). */
  result: RoutingResult | null;
  loading: boolean;
  /** Запрос упал (в том числе 401/403): блок скрыт, вопрос всё равно отправится — отдел определит бэкенд. */
  failed: boolean;
}

const IDLE: RoutePreviewState = { result: null, loading: false, failed: false };

/**
 * Предпросмотр определения отдела: debounce 700 мс, текст от 12 знаков, устаревший запрос
 * отменяется (AbortController). Выбор отдела вручную перезапрашивает сразу, без паузы.
 */
export function useRoutePreview(
  text: string,
  deptCode: string | null,
): RoutePreviewState {
  const trimmed = text.trim();
  const enough = trimmed.length >= ROUTE_PREVIEW_MIN_CHARS;
  const [state, setState] = useState<RoutePreviewState>(IDLE);
  const lastText = useRef("");

  useEffect(() => {
    if (!enough) {
      lastText.current = "";
      setState((s) => (s.result || s.loading || s.failed ? IDLE : s));
      return;
    }
    // Текст не менялся — значит, отдел выбрали/сбросили: отвечаем без паузы.
    const immediate = lastText.current === trimmed;
    lastText.current = trimmed;
    const ctl = new AbortController();
    setState((s) => ({ ...s, loading: true, failed: false }));
    const timer = setTimeout(
      () => {
        feedbackApi
          .routePreview({ text: trimmed, deptCode: deptCode ?? undefined }, ctl.signal)
          .then((result) => setState({ result, loading: false, failed: false }))
          .catch(() => {
            if (ctl.signal.aborted) return;
            setState({ result: null, loading: false, failed: true });
          });
      },
      immediate ? 0 : ROUTE_PREVIEW_DEBOUNCE_MS,
    );
    return () => {
      clearTimeout(timer);
      ctl.abort();
    };
  }, [trimmed, enough, deptCode]);

  return state;
}

const PILL_TONE: Record<MethodPill["tone"], string> = {
  words: "var(--fk-orange-hover)",
  ai: "var(--fk-info)",
  plain: "var(--fk-text-2)",
};

function Row({ k, children }: { k: string; children: ReactNode }): JSX.Element {
  return (
    <div className="fk-fb-row">
      <span className="fk-fb-row-k">{k}</span>
      <span className="fk-fb-row-v">{children}</span>
    </div>
  );
}

/** Блок «Кому уйдёт вопрос». Пока нечего показывать (короткий текст, ошибка) — ничего не рисует. */
export function RoutingCard({
  preview,
  manualDept,
  onPickDept,
  onResetDept,
}: {
  preview: RoutePreviewState;
  /** Отдел, выбранный вручную (null — определяет CRM). */
  manualDept: string | null;
  onPickDept: (code: string) => void;
  onResetDept: () => void;
}): JSX.Element | null {
  const [pickOpen, setPickOpen] = useState(false);
  const depts = useQuery({
    queryKey: ["feedback", "departments"],
    queryFn: () => feedbackApi.departments(),
    enabled: pickOpen,
    staleTime: 5 * 60_000,
  });
  const r = preview.result;

  if (!r) {
    return preview.loading ? (
      <div className="fk-fb-muted">Определяем отдел…</div>
    ) : null;
  }
  const method = routingMethod(r.routedBy, r.ai);

  return (
    <div
      className="fk-fb-route"
      aria-live="polite"
      data-loading={preview.loading ? "true" : "false"}
    >
      <div className="fk-fb-route-head">
        <b>Кому уйдёт вопрос</b>
        {method ? <Pill tone={PILL_TONE[method.tone]}>{method.label}</Pill> : null}
      </div>
      <Row k="Отдел">
        <b>{r.deptLabel}</b>
      </Row>
      <Row k="Почему">{routingWhy(r)}</Row>
      <Row k="Ответит">{routingAssignee(r.assignee)}</Row>
      {r.context.length ? <Row k="Приложим">{r.context.join(" · ")}</Row> : null}
      <Row k="Срок ответа">
        <b className={r.urgent ? "fk-fb-urgent" : undefined}>{previewDueText(r)}</b>
      </Row>
      <div className="fk-fb-route-links">
        <button
          type="button"
          className="fk-fb-link"
          onClick={() => setPickOpen(!pickOpen)}
        >
          {pickOpen ? "Свернуть" : "Не туда? Выбрать отдел самому"}
        </button>
        {manualDept ? (
          <button
            type="button"
            className="fk-fb-link"
            onClick={() => {
              setPickOpen(false);
              onResetDept();
            }}
          >
            Определить отдел автоматически
          </button>
        ) : null}
      </div>
      {pickOpen ? (
        <div className="fk-fb-chips">
          {depts.isLoading ? (
            <span className="fk-fb-muted">Загружаем отделы…</span>
          ) : null}
          {(depts.data ?? []).map((d) => (
            <Chip
              key={d.code}
              size="sm"
              active={d.code === r.deptCode}
              onClick={() => {
                setPickOpen(false);
                onPickDept(d.code);
              }}
            >
              {d.label}
            </Chip>
          ))}
        </div>
      ) : null}
    </div>
  );
}
